import * as React from 'react';
import Dialog from '@material-ui/core/Dialog';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import * as moment from 'moment';
import { useDateFormat } from '../../hooks/useDateFormat';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Countdown from 'react-countdown';
import Badge from '../Dashboard/badge';
import CompanyLogo from '../Dashboard/company-logo';
import { createGoogleMapsLink } from '../Shared/getMapsLink';

const styles = makeStyles((theme) => ({
  header: {
    textTransform: 'uppercase',
    color: '#fff',
    width: '70%',
    float: 'left',
  },
  content: {
    marginTop: '20px',
  },
  infoOverlay: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    borderRadius: 16,
    padding: 16,
    width: '92%',
    maxWidth: 560,
    margin: '16px auto',
  },
  actionsOverlay: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    borderRadius: 16,
    padding: 16,
    width: '92%',
    maxWidth: 560,
    margin: '16px auto',
  },
  infoOverlay: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    borderRadius: 16,
    padding: 16,
  },
  descriptionField: {
    fontWeight: 'bold',
    textTransform: 'lowercase',

    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem',
    },
  },
  descriptionValue: {
    lineHeight: '1rem',

    [theme.breakpoints.up('sm')]: {
      lineHeight: '1.5rem',
      fontSize: '1.2rem',
    },
  },
}));

const StatusAnnouncedDialog = ({ invitation }) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (
      !invitation.can_announce_arrival &&
      invitation.announce_arrival_status === 'GUEST_NOTIFIED_ARRIVAL'
    ) {
      setOpen(true);
    }
  }, [invitation]);

  return (
    <Dialog open={open} size="md" fullWidth onClose={() => setOpen(false)}>
      <Box p={2}>
        <Typography align="center">
          <b>{t('Your arrival has been notified')}</b>
        </Typography>
        <Typography align="center">
          {t(
            'When your access is authorized you will be able to access directly with the QR code that will appear here'
          )}
        </Typography>
      </Box>
      <Box p={2} textAlign="center">
        <Button type="button" onClick={() => setOpen(false)} variant="contained" color="primary">
          {t('Close')}
        </Button>
      </Box>
    </Dialog>
  );
};

const StatusHostDelayedDialog = ({ invitation }) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!invitation.can_announce_arrival && invitation.announce_arrival_status === 'HOST_DELAYED') {
      setOpen(true);
    }
  }, [invitation]);

  let minutes = 0;
  try {
    const last = invitation.events[invitation.events.length - 1];

    minutes = moment(last.due_date).diff(moment(last.date), 'minutes');
  } catch (e) {
    console.log(e);
  }

  return (
    <Dialog open={open} size="md" fullWidth onClose={() => setOpen(false)}>
      <Box p={2}>
        <Typography align="center">
          {t('Your host requested for you to wait {{minutes}} minutes', { minutes })}
        </Typography>
      </Box>
      <Box p={2} textAlign="center">
        <Button type="button" onClick={() => setOpen(false)} variant="contained" color="primary">
          {t('Close')}
        </Button>
      </Box>
    </Dialog>
  );
};

const Timer = ({ invitation }) => {
  if (invitation.announce_arrival_status !== 'HOST_DELAYED') {
    return null;
  }

  const last = invitation.events[invitation.events.length - 1];

  return (
    <Box style={{ float: 'right' }}>
      <Typography variant="h4" style={{ color: 'white' }}>
        <Countdown
          date={last.due_date}
          renderer={({ formatted }) => {
            return `${formatted.minutes}:${formatted.seconds}`;
          }}
        />
      </Typography>
    </Box>
  );
};

export default ({ invitation, isLoading, cancel, confirm, reject, announceArrival }) => {
  if (isLoading) {
    return <div></div>;
  }

  const { t, i18n } = useTranslation();
  const { timeFormat, formatTime } = useDateFormat();
  const mapsLink = createGoogleMapsLink(`${invitation.Building.address}`);

  const classes = styles();
  moment.locale(i18n.language.substr(0, 2));

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={12} sm={9} md={8} lg={6}>
        <Badge
          user={invitation.Guest}
          accessCode={invitation.AccessCode}
          params={{ invitation }}
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <CompanyLogo company={invitation.Company} />
              <Timer invitation={invitation} />
            </div>
          }
          backgroundImage={
            invitation.Company && invitation.Company.background
              ? invitation.Company.background
              : null
          }
        >
          <div className={`${classes.content}`}>
            <div className={`${classes.infoOverlay}`}>
              <Grid container spacing={2} justify="center">
                <Grid item xs={3} md={2}>
                  <Typography variant="body2" className={classes.descriptionField}>
                    {t('Invited by')}
                  </Typography>
                </Grid>
                <Grid item xs={7} md={4}>
                  <Typography align="right" className={classes.descriptionValue}>
                    {invitation.Host.name} {invitation.Host.surname}
                    {invitation.Host.company && (
                      <>
                        <br />
                        {invitation.Host.company}
                      </>
                    )}
                    {invitation.Host.company_role && (
                      <>
                        <br />
                        {invitation.Host.company_role}
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} justify="center">
                <Grid item xs={3} md={2}>
                  <Typography variant="body2" className={classes.descriptionField}>
                    {t('Cellphone')}
                  </Typography>
                </Grid>
                <Grid item xs={7} md={4}>
                  <Typography align="right" className={classes.descriptionValue}>
                    {invitation.Host.cellphone}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} justify="center">
                <Grid item xs={3} md={2}>
                  <Typography variant="body2" className={classes.descriptionField}>
                    {t('Meeting date')}
                  </Typography>
                </Grid>
                <Grid item xs={7} md={4}>
                  <Typography align="right" className={classes.descriptionValue}>
                    {moment(invitation.date_from).format(`ddd DD MMM  ${timeFormat}`)} -{' '}
                    {formatTime(invitation.date_to)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} justify="center">
                <Grid item xs={3} md={2}>
                  <Typography variant="body2" className={classes.descriptionField}>
                    {t('Building')}
                  </Typography>
                </Grid>
                <Grid item xs={7} md={4}>
                  <a href={mapsLink} target="_blank">
                    <Typography align="right" className={classes.descriptionValue}>
                      {invitation.Building.name}
                      <br />
                      {invitation.Building.address}{' '}
                      {invitation.Company && (
                        <>
                          {invitation.Company.floor} - {invitation.Company.room}
                        </>
                      )}
                    </Typography>
                  </a>
                </Grid>
              </Grid>
              <StatusAnnouncedDialog invitation={invitation} />
              <StatusHostDelayedDialog invitation={invitation} />
            </div>
            <div className={classes.actionsOverlay}>
              <Grid container spacing={2} justify="center">
                {invitation.can_announce_arrival && (
                  <Grid item xs={12}>
                    <Button
                      type="button"
                      color="primary"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => announceArrival(invitation.id)}
                    >
                      {t('Announce arrival')}
                    </Button>
                  </Grid>
                )}
                {invitation.can_confirm && confirm && (
                  <Grid item xs={12} md={6}>
                    <Button
                      type="button"
                      color="primary"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => confirm(invitation.id)}
                    >
                      {t('Confirm')}
                    </Button>
                  </Grid>
                )}
                {invitation.can_reject && reject && (
                  <Grid item xs={12} md={6}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => reject(invitation.id)}
                    >
                      {t('Reject')}
                    </Button>
                  </Grid>
                )}
                {invitation.can_cancel && cancel && (
                  <Grid item xs={12}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => cancel(invitation.id)}
                    >
                      {t('Cancel')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </div>
          </div>
        </Badge>
      </Grid>
    </Grid>
  );
};
